import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { Mail, Lock, User, Phone, Heart, ArrowLeft } from "lucide-react";

const CustomerAuth = ({ mode = "login" }) => {
  const { customerLogin, customerRegister, lang } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", confirm: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const isRegister = mode === "register";

  const submitForgot = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setForgotSent(true);
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : "Gagal mengirim email");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (isRegister && form.password !== form.confirm) {
      setErr(lang === "id" ? "Password tidak cocok" : "Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        await customerRegister({ email: form.email, password: form.password, name: form.name, phone: form.phone });
      } else {
        await customerLogin(form.email, form.password);
      }
      const next = new URLSearchParams(location.search).get("next") || "/account";
      navigate(next, { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || (lang === "id" ? "Gagal" : "Failed"));
    } finally { setLoading(false); }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="relative min-h-[80vh] flex items-center justify-center px-5 py-12 overflow-hidden">
      {/* Festive background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 text-7xl opacity-10 select-none">🎄</div>
        <div className="absolute top-32 right-20 text-5xl opacity-10 select-none">✨</div>
        <div className="absolute bottom-20 left-1/4 text-6xl opacity-10 select-none">🎁</div>
        <div className="absolute bottom-40 right-1/3 text-5xl opacity-10 select-none">⭐</div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">
            ✦ Holy Flowy ✦
          </div>
          <h1 className="font-serif text-4xl text-holy-red leading-tight">
            {forgot
              ? (lang === "id" ? "Lupa Password" : "Forgot Password")
              : isRegister
                ? (lang === "id" ? "Buat Akun Baru" : "Create Account")
                : (lang === "id" ? "Selamat Datang Kembali" : "Welcome Back")}
          </h1>
          <p className="font-script text-2xl text-foreground/70 mt-1">handmade with love</p>
        </div>

        {forgot ? (
          <form onSubmit={submitForgot} className="bg-white rounded-2xl border border-border/60 shadow-soft p-6 sm:p-8 space-y-4" data-testid="forgot-form">
            {forgotSent ? (
              <div className="text-center space-y-3" data-testid="forgot-success">
                <div className="text-4xl">📧</div>
                <p className="text-sm text-foreground">
                  {lang === "id"
                    ? "Link reset password telah dikirim ke email Anda. Silakan cek inbox (atau folder spam). Link berlaku 1 jam."
                    : "A reset link has been sent to your email. Please check your inbox (or spam folder). Link valid for 1 hour."}
                </p>
                <button type="button" onClick={() => { setForgot(false); setForgotSent(false); }} className="text-holy-red text-sm font-semibold hover:underline" data-testid="forgot-back">
                  ← {lang === "id" ? "Kembali ke halaman Masuk" : "Back to Sign in"}
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {lang === "id"
                    ? "Masukkan email akun Anda. Kami akan mengirimkan link untuk membuat password baru."
                    : "Enter your account email. We'll send you a link to create a new password."}
                </p>
                <FieldIcon icon={Mail} type="email" placeholder="Email" value={form.email} onChange={update("email")} required testid="forgot-email" />
                {err && <div className="text-sm text-holy-red bg-holy-red/5 border border-holy-red/20 rounded-lg px-3 py-2" data-testid="forgot-error">⚠️ {err}</div>}
                <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-holy-red hover:bg-holy-red-dark text-ivory rounded-full py-3 font-medium transition-all disabled:opacity-60">
                  {loading ? "..." : (lang === "id" ? "Kirim Link Reset" : "Send Reset Link")}
                </button>
                <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-muted-foreground hover:text-holy-red inline-flex items-center justify-center gap-1" data-testid="forgot-cancel">
                  <ArrowLeft className="w-3.5 h-3.5" /> {lang === "id" ? "Kembali" : "Back"}
                </button>
              </>
            )}
          </form>
        ) : (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-border/60 shadow-soft p-6 sm:p-8 space-y-4" data-testid={isRegister ? "register-form" : "login-form"}>
          {isRegister && (
            <>
              <FieldIcon icon={User} placeholder={lang === "id" ? "Nama Lengkap" : "Full Name"} value={form.name} onChange={update("name")} required testid="auth-name" />
              <FieldIcon icon={Phone} placeholder={lang === "id" ? "No. WhatsApp (opsional)" : "WhatsApp (optional)"} value={form.phone} onChange={update("phone")} testid="auth-phone" />
            </>
          )}
          <FieldIcon icon={Mail} type="email" placeholder="Email" value={form.email} onChange={update("email")} required testid="auth-email" />
          <FieldIcon icon={Lock} type="password" placeholder="Password" value={form.password} onChange={update("password")} required testid="auth-password" />
          {isRegister && (
            <FieldIcon icon={Lock} type="password" placeholder={lang === "id" ? "Konfirmasi Password" : "Confirm Password"} value={form.confirm} onChange={update("confirm")} required testid="auth-confirm" />
          )}

          {!isRegister && (
            <div className="text-right -mt-1">
              <button
                type="button"
                onClick={() => { setForgot(true); setErr(""); }}
                className="text-xs text-holy-gold-dark hover:text-holy-red font-semibold"
                data-testid="link-forgot-password"
              >
                {lang === "id" ? "Lupa Password?" : "Forgot Password?"}
              </button>
            </div>
          )}

          {err && <div className="text-sm text-holy-red bg-holy-red/5 border border-holy-red/20 rounded-lg px-3 py-2" data-testid="auth-error">⚠️ {err}</div>}

          <button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-holy-red hover:bg-holy-red-dark text-ivory rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
            {loading ? "..." : (isRegister ? (lang === "id" ? "Daftar Sekarang" : "Sign Up") : (lang === "id" ? "Masuk" : "Sign In"))}
          </button>

          <div className="text-center text-sm text-muted-foreground pt-2 border-t border-border/40">
            {isRegister ? (
              <>
                {lang === "id" ? "Sudah punya akun?" : "Already have an account?"}{" "}
                <Link to="/login" className="text-holy-red font-semibold hover:underline" data-testid="link-login">{lang === "id" ? "Masuk" : "Sign in"}</Link>
              </>
            ) : (
              <>
                {lang === "id" ? "Belum punya akun?" : "Don't have an account?"}{" "}
                <Link to="/register" className="text-holy-red font-semibold hover:underline" data-testid="link-register">{lang === "id" ? "Daftar Gratis" : "Sign up free"}</Link>
              </>
            )}
          </div>
        </form>
        )}

        <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
          <Heart className="w-3 h-3 text-holy-red fill-holy-red" /> {lang === "id" ? "Bergabung dengan komunitas Holy Flowy" : "Join the Holy Flowy community"}
        </p>
      </div>
    </div>
  );
};

const FieldIcon = ({ icon: Icon, testid, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-cream border border-border rounded-xl pl-10 pr-4 py-3 text-sm focus:bg-white focus:border-holy-red focus:ring-2 focus:ring-holy-red/10 outline-none transition-all"
    />
  </div>
);

export default CustomerAuth;
